import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

import {
  EpidemiologyError,
  IPatientHistory,
  PatientRequestsService,
  SensitivityATBResponse,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import {
  formatPatientRequestDateOnly,
  formatPatientRequestDateTime,
} from '../patient-request-date-format.util';

import {
  buildSensitivityPivot,
  lookupCell,
  type AtbCatalogRow,
  type SensitivityColumnMeta,
  type SensitivityPivotModel,
} from './sensitivity-atb-pivot';
import { buildSensitivityAtbCsv } from './sensitivity-atb-csv';

const EMPTY_PIVOT: SensitivityPivotModel = { catalog: [], columns: [], cells: {} };
type SensitivityDateInput = string | Date | null | undefined;

@Component({
  selector: 'afoone-sensitivity-atb-view',
  templateUrl: './sensitivity-atb-view.component.html',
  styleUrls: ['./sensitivity-atb-view.component.css'],
})
export class SensitivityATBViewComponent implements OnInit, OnDestroy {
  patient: IPatientHistory | null = null;
  loading = true;
  error: string | null = null;

  pivot: SensitivityPivotModel = { ...EMPTY_PIVOT };

  rawMaxCol = 0;
  rawMaxRow = 0;

  selectedMicrobes: string[] = [];
  selectedMaterials: string[] = [];
  atbRowFilter = '';

  /** Referencias estables para PrimeNG MultiSelect (evita reset al cambiar CD). */
  microbeOptions: { label: string; value: string }[] = [];
  materialOptions: { label: string; value: string }[] = [];

  patientId = '';
  private labNr = 0;
  private dateFrom = '';
  private dateTo = '';

  private destroyed$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private service: PatientRequestsService,
    private keycloak: KeycloakService,
    private userService: UsersService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private msgbox: MsgboxService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(async (qp) => {
      this.patientId = String(qp['patientId'] ?? '').trim();
      this.labNr = Number(qp['labNr'] ?? 0);
      this.dateFrom = String(qp['dateFrom'] ?? '').trim();
      this.dateTo = String(qp['dateTo'] ?? '').trim();
      const c = qp['centroId'];
      if (c != null && c !== '') {
        const n = Number(c);
        if (!Number.isNaN(n)) this.userService.setSisifoDefaultCenter(n);
      }

      if (!this.patientId || !this.labNr || !this.dateFrom || !this.dateTo) {
        this.loading = false;
        this.error = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.MISSING_PARAMS');
        return;
      }

      await this.loadAll();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get dateRangeLabel(): string {
    if (!this.dateFrom || !this.dateTo) return '';
    return `${this.formatDateOnlyDisplay(this.dateFrom)} - ${this.formatDateOnlyDisplay(this.dateTo)}`;
  }

  private async waitForToken(maxWaitMs = 5000): Promise<boolean> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const token = await this.keycloak.getToken();
      if (token) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  private async loadAll(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.pivot = { ...EMPTY_PIVOT };
    this.selectedMicrobes = [];
    this.selectedMaterials = [];
    this.atbRowFilter = '';
    this.rebuildFilterOptions();

    const hasToken = await this.waitForToken();
    if (!hasToken) {
      this.loading = false;
      this.error = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.NO_SESSION_TAB');
      return;
    }

    this.service
      .getPatientHistories({
        history: this.patientId,
        searchType: 'C',
        centroId: this.userService.getSisifoDefaultCenter(),
      } as any)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (list) => {
          const historiaNorm = this.patientId.trim();
          this.patient = Array.isArray(list)
            ? list.find((p) => (p.historia?.trim() ?? '') === historiaNorm) ?? null
            : null;

          this.service
            .searchSensitivityATB({
              patientId: this.patientId,
              labNr: this.labNr,
              dateFrom: this.dateFrom,
              dateTo: this.dateTo,
              centroId: this.userService.getSisifoDefaultCenter(),
            })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              next: (res: SensitivityATBResponse) => {
                this.loading = false;
                this.rawMaxCol = res.maxCol ?? 0;
                this.rawMaxRow = res.maxRow ?? 0;
                const data = Array.isArray(res.data) ? res.data : [];
                this.pivot =
                  data.length > 0
                    ? buildSensitivityPivot(data)
                    : { ...EMPTY_PIVOT };
                this.rebuildFilterOptions();
              },
              error: (err: unknown) => {
                this.loading = false;
                this.applySensitivityError(err);
              },
            });
        },
        error: () => {
          this.loading = false;
          this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
        },
      });
  }

  private applySensitivityError(err: unknown): void {
    if (err instanceof EpidemiologyError) {
      if (err.msgCode === 'SENSITIVITY_ATB_NOT_FOUND') {
        this.toastr.info(this.translate.instant(`WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}`));
        return;
      }
      const key =
        err.msgCode === 'DB_ERROR_SEARCHING' || err.msgCode === 'DB_ERROR'
          ? `WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}`
          : `WEB_RESULTS.EPIDEMIOLOGY.DB_ERROR`;
      this.msgbox.showError(key);
      return;
    }
    this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
  }

  /**
   * Clave estable por columna para el combo de microorganismos.
   * Si el SP deja `Isolate` vacío, antes no había opciones (no se podía filtrar).
   */
  microbeColumnKey(col: SensitivityColumnMeta): string {
    const iso = String(col.isolate ?? '').trim();
    return iso || `__mic_rc_${col.requisCol}`;
  }

  /** Igual para tipo de muestra cuando `MaterialName` viene vacío. */
  materialColumnKey(col: SensitivityColumnMeta): string {
    const mat = String(col.materialName ?? '').trim();
    return mat || `__mat_rc_${col.requisCol}`;
  }

  private microbeOptionLabel(col: SensitivityColumnMeta): string {
    const iso = String(col.isolate ?? '').trim();
    if (iso) return iso;
    const noIso = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.NO_ISOLATE');
    const nr = String(col.requisNr ?? '').trim();
    return nr ? `${noIso} (${nr})` : `${noIso} #${col.requisCol}`;
  }

  private materialOptionLabel(col: SensitivityColumnMeta): string {
    const mat = String(col.materialName ?? '').trim();
    if (mat) return mat;
    const noMat = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.NO_MATERIAL_NAME');
    const nr = String(col.requisNr ?? '').trim();
    return nr ? `${noMat} (${nr})` : `${noMat} #${col.requisCol}`;
  }

  /** Columnas disponibles como opciones de microorganismo tras filtrar por tipo de muestra. */
  private columnsForMicrobeOptions(): SensitivityColumnMeta[] {
    if (!this.selectedMaterials?.length) return [...this.pivot.columns];
    return this.pivot.columns.filter((c) => this.selectedMaterials.includes(this.materialColumnKey(c)));
  }

  /** Columnas disponibles como opciones de muestra tras filtrar por microorganismo. */
  private columnsForMaterialOptions(): SensitivityColumnMeta[] {
    if (!this.selectedMicrobes?.length) return [...this.pivot.columns];
    return this.pivot.columns.filter((c) => this.selectedMicrobes.includes(this.microbeColumnKey(c)));
  }

  /** Rellena `microbeOptions` / `materialOptions` (llamar tras cargar pivot o cambiar selección cruzada). */
  private rebuildFilterOptions(): void {
    const microbeSeen = new Set<string>();
    const microbeOut: { label: string; value: string }[] = [];
    for (const c of this.columnsForMicrobeOptions()) {
      const id = this.microbeColumnKey(c);
      if (microbeSeen.has(id)) continue;
      microbeSeen.add(id);
      microbeOut.push({ label: this.microbeOptionLabel(c), value: id });
    }
    microbeOut.sort((a, b) => a.label.localeCompare(b.label));
    this.microbeOptions = microbeOut;

    const matSeen = new Set<string>();
    const matOut: { label: string; value: string }[] = [];
    for (const c of this.columnsForMaterialOptions()) {
      const id = this.materialColumnKey(c);
      if (matSeen.has(id)) continue;
      matSeen.add(id);
      matOut.push({ label: this.materialOptionLabel(c), value: id });
    }
    matOut.sort((a, b) => a.label.localeCompare(b.label));
    this.materialOptions = matOut;
  }

  get visibleColumns(): SensitivityColumnMeta[] {
    let v = [...this.pivot.columns];
    if (this.selectedMicrobes?.length) {
      v = v.filter((c) => this.selectedMicrobes.includes(this.microbeColumnKey(c)));
    }
    if (this.selectedMaterials?.length) {
      v = v.filter((c) => this.selectedMaterials.includes(this.materialColumnKey(c)));
    }
    return v;
  }

  get visibleCatalogRows(): AtbCatalogRow[] {
    const q = (this.atbRowFilter ?? '').trim().toLowerCase();
    const base = this.pivot.catalog;
    if (!q) return base;
    return base.filter((r) => r.atbName.toLowerCase().includes(q));
  }

  onCrossFilterChange(): void {
    /** Tras el tick de Angular / actualización de ngModel en PrimeNG (evita vaciar la selección al elegir). */
    setTimeout(() => {
      this.rebuildFilterOptions();
      const matOk = new Set(this.materialOptions.map((o) => o.value));
      this.selectedMaterials = (this.selectedMaterials ?? []).filter((m) => matOk.has(m));
      const micOk = new Set(this.microbeOptions.map((o) => o.value));
      this.selectedMicrobes = (this.selectedMicrobes ?? []).filter((m) => micOk.has(m));
      this.rebuildFilterOptions();
    }, 0);
  }

  formatEntryDateDisplay(v: SensitivityDateInput): string {
    return formatPatientRequestDateTime(v);
  }

  private formatDateOnlyDisplay(v: SensitivityDateInput): string {
    return formatPatientRequestDateOnly(v);
  }

  columnHeaderLines(col: SensitivityColumnMeta): string[] {
    const lines: string[] = [col.requisNr];
    const dt = this.formatEntryDateDisplay(col.entryDate);
    if (dt) lines.push(dt);
    if (col.isolate) lines.push(col.isolate);
    if (col.materialName) lines.push(col.materialName);
    return lines;
  }

  cellState(requisCol: number, testRow: number) {
    return lookupCell(this.pivot, requisCol, testRow);
  }

  get canExport(): boolean {
    return this.visibleCatalogRows.length > 0 && this.visibleColumns.length > 0;
  }

  /** Columnas de petición con ancho fijo (mismo patrón que Acumulados: `--satb-data-cols` × 160px). */
  get satbDataColsVar(): number {
    return Math.max(this.visibleColumns.length, 1);
  }

  exportCsv(): void {
    if (!this.canExport) return;
    const csvBody = buildSensitivityAtbCsv(this.pivot, this.visibleCatalogRows, this.visibleColumns);
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvBody], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.defaultExportFilename();
    a.click();
    URL.revokeObjectURL(url);
  }

  private defaultExportFilename(): string {
    const nhc = String(this.patientId ?? '').replaceAll(/[^\w-]/g, '_').slice(0, 32);
    const pa = isoYyyyMmDdToDdMmYyyy(this.dateFrom);
    const pb = isoYyyyMmDdToDdMmYyyy(this.dateTo);
    return `Tabla Antibiogramas_${nhc}_${pa}_${pb}.csv`;
  }
}

function isoYyyyMmDdToDdMmYyyy(iso: string): string {
  const trimmed = String(iso).trim();
  const re = /^(\d{4})-(\d{2})-(\d{2})/;
  const m = re.exec(trimmed);
  if (!m) return trimmed.replaceAll(/\D/g, '') || '00000000';
  return `${m[3]}${m[2]}${m[1]}`;
}
